/**
 * The viewport.
 *
 * Four rules shape this file:
 *
 *  1. One render per animation frame, never more. A slider drag produces a
 *     recipe change per pointer move; rendering each one would queue frames the
 *     GPU can never catch up on, and the image would lag the hand.
 *  2. Measurement happens only after the hand stops. The histogram, the stats
 *     the planner reads and the preview it is sent all come from the same
 *     settled frame, so the numbers and the picture can never disagree.
 *  3. Before/after shows the *unedited* frame. The renderer's own wipe compares
 *     against its intermediate base pass, which already carries exposure and
 *     tone, so the comparison here is drawn from a real neutral render instead.
 *     Crop, rotation and orientation stay on both sides: the question is what
 *     the grade did, not where the frame was cut.
 *  4. The divider is dragged, not held. It follows the pointer from anywhere on
 *     the stage and nothing transitions its position, because a comparison you
 *     can only see while a key is down is a comparison you cannot study.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type {
  CSSProperties,
  KeyboardEvent as ReactKeyboardEvent,
  MouseEvent as ReactMouseEvent,
  PointerEvent as ReactPointerEvent,
} from 'react';
import {
  computeImageStats,
  formatCapture,
  type DecodedImage,
} from '@lumos/core';
import { LumosRenderer, type RenderOptions, type ViewState } from '../engine/renderer.ts';
import {
  CLEAN_OPTIONS,
  EMPTY_LIVE,
  PROXY_EDGE,
  PROXY_QUALITY,
  cacheProxy,
  clearHost,
  geometryKey,
  neutralWithGeometry,
  proxyCanvas,
  registerHost,
  toImageData,
  type Host,
  type Live,
} from './canvasBridge.ts';
import { Tip } from './controls/Tooltip.tsx';
import { useSelectedPhoto, useStore } from '../state/store.ts';

/** Quiet period after the last change before the frame counts as settled. */
const SETTLE_MS = 180;
const MIN_ZOOM = 0.2;
const MAX_ZOOM = 24;
const ZOOM_STOPS = [50, 100, 200];
const SPLIT_MIN = 0.02;
const SPLIT_MAX = 0.98;

function describe(error: unknown): string {
  return error instanceof Error ? error.message : String(error);
}

function clamp(value: number, low: number, high: number): number {
  return Math.min(high, Math.max(low, value));
}

function reducedMotion(): boolean {
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

interface Notice {
  /** Deduplicates two decoder notes that mean the same thing to the photographer. */
  key: string;
  text: string;
  /** Warn is reserved for "you are not editing the sensor data". The rest is a fact. */
  tone: 'info' | 'warn';
  tip?: { title: string; body: string };
}

/**
 * Decoder notes are written for accuracy, in full sentences. Along the top of a
 * photograph that is a wall of text over the picture, so the long form moves
 * into the tooltip and the strip keeps only what changes what you do next.
 */
function noticeFor(warning: string): Notice | null {
  if (warning.includes('half-resolution')) {
    return {
      key: 'half',
      text: 'Editing a preview. Export uses full resolution.',
      tone: 'info',
      tip: {
        title: 'Half-size preview',
        body: 'The sensor was read at half size to keep editing quick. Export re-decodes the file at full size.',
      },
    };
  }
  if (warning.includes('white-balance matrices')) {
    return {
      key: 'balance',
      text: 'White balance is approximate.',
      tone: 'info',
      tip: {
        title: 'White balance',
        body: "The camera's own values could not be read, so temperature is shown against a 5500 K reference.",
      },
    };
  }
  if (warning.includes('8-bit')) {
    return {
      key: 'depth',
      text: '8-bit file. Less room to push exposure.',
      tone: 'warn',
      tip: {
        title: '8-bit file',
        body: 'Highlights and shadows clip sooner here than they would in a raw capture.',
      },
    };
  }
  // Both of these are already said by the notice `fromEmbeddedPreview` raises.
  if (warning.includes('embedded preview') || warning.includes('Sensor data could not be decoded')) {
    return null;
  }
  // Nothing the photographer can act on, and the facts strip already shows what is missing.
  if (warning.includes('Capture metadata')) return null;
  return { key: warning, text: warning, tone: 'warn' };
}

/** Where the frame sits inside the canvas, in the same terms the present shader uses. */
function placement(
  processed: { width: number; height: number },
  deviceWidth: number,
  deviceHeight: number,
  view: ViewState,
) {
  const imageAspect = processed.width / processed.height;
  const canvasAspect = deviceWidth / deviceHeight;
  const fitX = imageAspect > canvasAspect ? 1 : imageAspect / canvasAspect;
  const fitY = imageAspect > canvasAspect ? canvasAspect / imageAspect : 1;
  const scaleX = fitX * view.zoom;
  const scaleY = fitY * view.zoom;
  return {
    fitX,
    fitY,
    scaleX,
    scaleY,
    offsetX: (1 - scaleX) / 2 + view.panX,
    offsetY: (1 - scaleY) / 2 - view.panY,
  };
}

/** An axis that fits inside the viewport stays centred; only an overflowing one pans. */
function settleOffset(offset: number, scale: number): number {
  return scale > 1 ? clamp(offset, 1 - scale, 0) : (1 - scale) / 2;
}

export function Canvas() {
  const photo = useSelectedPhoto();
  const view = useStore((state) => state.view);
  const showClipping = useStore((state) => state.showClipping);
  const showBefore = useStore((state) => state.showBefore);
  const splitPosition = useStore((state) => state.splitPosition);
  const activeMaskIndex = useStore((state) => state.activeMaskIndex);
  const showMaskOverlay = useStore((state) => state.showMaskOverlay);
  const setView = useStore((state) => state.setView);
  const zoomTo = useStore((state) => state.zoomTo);
  const toggleClipping = useStore((state) => state.toggleClipping);
  const setShowBefore = useStore((state) => state.setShowBefore);
  const setSplitPosition = useStore((state) => state.setSplitPosition);
  const select = useStore((state) => state.select);

  const image = photo?.image ?? null;
  const recipe = photo?.recipe ?? null;

  const canvasRef = useRef<HTMLCanvasElement>(null);
  const beforeRef = useRef<HTMLCanvasElement>(null);
  const viewportRef = useRef<HTMLDivElement>(null);
  const rendererRef = useRef<LumosRenderer | null>(null);
  const uploadedRef = useRef<DecodedImage | null>(null);
  const liveRef = useRef<Live>(EMPTY_LIVE);
  const processedRef = useRef<{ width: number; height: number } | null>(null);
  const frameRef = useRef(0);
  const settleRef = useRef(0);
  const reportedAtRef = useRef(0);
  const renderErrorRef = useRef<string | null>(null);
  const dragRef = useRef<{ kind: 'pan' | 'split'; x: number; y: number } | null>(null);

  /** Bumped to mint a fresh canvas element when the old one's context is unusable. */
  const [surface, setSurface] = useState(0);
  const rebuildsRef = useRef(0);
  const [initError, setInitError] = useState<string | null>(null);
  const [renderError, setRenderError] = useState<string | null>(null);
  const [renderMs, setRenderMs] = useState<number | null>(null);
  const [processed, setProcessed] = useState<{ width: number; height: number } | null>(null);
  const [size, setSize] = useState({ cssWidth: 0, cssHeight: 0, deviceWidth: 0, deviceHeight: 0 });
  const [beforeKey, setBeforeKey] = useState<string | null>(null);
  const [panning, setPanning] = useState(false);
  const [splitting, setSplitting] = useState(false);
  const [entered, setEntered] = useState(false);
  const [spaceHeld, setSpaceHeld] = useState(false);
  const [contextLost, setContextLost] = useState(false);

  const options = useMemo<RenderOptions>(
    () => ({
      showClipping,
      overlayMask: showMaskOverlay ? activeMaskIndex : -1,
      overlayStrength: showMaskOverlay && activeMaskIndex >= 0 ? 1 : 0,
      // The before/after comparison is drawn over the canvas, not inside it.
      splitPosition: null,
    }),
    [showClipping, showMaskOverlay, activeMaskIndex],
  );

  // --- rendering -----------------------------------------------------------

  /**
   * The source texture belongs to one renderer instance, and the first draw is a
   * frame away. Anything that renders outside the draw loop, the unedited
   * comparison and the planner's proxy, has to make sure the upload has happened.
   */
  const ensureUploaded = useCallback((renderer: LumosRenderer, image: DecodedImage) => {
    if (uploadedRef.current === image) return;
    renderer.setImage(image);
    uploadedRef.current = image;
  }, []);

  const drawNow = useCallback(() => {
    const renderer = rendererRef.current;
    const canvas = canvasRef.current;
    const live = liveRef.current;
    if (!renderer || !canvas || !live.image || !live.recipe) return;
    if (renderer.contextLost) {
      setContextLost(true);
      return;
    }

    const started = performance.now();
    try {
      ensureUploaded(renderer, live.image);
      const dimensions = renderer.process(live.recipe, live.image, live.options);
      renderer.present(live.view, live.options, canvas.width, canvas.height);

      processedRef.current = dimensions;
      setProcessed((previous) =>
        previous && previous.width === dimensions.width && previous.height === dimensions.height
          ? previous
          : dimensions,
      );

      const elapsed = performance.now() - started;
      // Reporting every frame would re-render this component mid-drag for a number.
      if (started - reportedAtRef.current > 400) {
        reportedAtRef.current = started;
        setRenderMs(elapsed);
      }
      if (renderErrorRef.current !== null) {
        renderErrorRef.current = null;
        setRenderError(null);
      }
    } catch (error) {
      const message = describe(error);
      if (renderErrorRef.current !== message) {
        renderErrorRef.current = message;
        setRenderError(message);
      }
    }
  }, [ensureUploaded]);

  const settle = useCallback(() => {
    const renderer = rendererRef.current;
    const live = liveRef.current;
    if (!renderer || !live.image || !live.recipe || !live.photoId || renderer.contextLost) return;

    const overlaid = live.options.showClipping || live.options.overlayMask >= 0;
    try {
      ensureUploaded(renderer, live.image);
      if (overlaid) renderer.process(live.recipe, live.image, CLEAN_OPTIONS);
      const frame = renderer.readProcessed();
      if (overlaid) drawNow();
      if (!frame) return;

      const canvas = proxyCanvas(frame, PROXY_EDGE);
      const context = canvas.getContext('2d', { willReadFrequently: true });
      if (!context) return;
      const pixels = context.getImageData(0, 0, canvas.width, canvas.height);
      useStore.getState().setStats(computeImageStats(pixels.data, canvas.width, canvas.height));
      cacheProxy(live.photoId, live.recipe, canvas.toDataURL('image/jpeg', PROXY_QUALITY));
    } catch (error) {
      // Measurement is best-effort; a failure here must not take the viewport down.
      console.warn('Lumos could not measure the settled frame', error);
    }
  }, [drawNow, ensureUploaded]);

  const schedule = useCallback(() => {
    if (frameRef.current !== 0) return;
    frameRef.current = window.requestAnimationFrame(() => {
      frameRef.current = 0;
      drawNow();
      window.clearTimeout(settleRef.current);
      settleRef.current = window.setTimeout(settle, SETTLE_MS);
    });
  }, [drawNow, settle]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    let renderer: LumosRenderer;
    try {
      renderer = new LumosRenderer(canvas);
    } catch (error) {
      // A canvas whose context was disposed keeps handing that dead context back
      // for ever. It happens on every hot reload in development and after a
      // background tab is reclaimed, and only a new element recovers from it.
      const existing = canvas.getContext('webgl2');
      if (existing?.isContextLost() && rebuildsRef.current < 3) {
        rebuildsRef.current += 1;
        setSurface((generation) => generation + 1);
        return;
      }
      setInitError(describe(error));
      return;
    }
    setInitError(null);
    setContextLost(false);
    rendererRef.current = renderer;
    const mounted: Host = {
      renderer,
      live: () => liveRef.current,
      redraw: drawNow,
      ensure: () => {
        const live = liveRef.current;
        if (live.image) ensureUploaded(renderer, live.image);
      },
    };
    registerHost(mounted);

    const onLost = () => setContextLost(true);
    canvas.addEventListener('webglcontextlost', onLost);

    return () => {
      canvas.removeEventListener('webglcontextlost', onLost);
      clearHost(mounted);
      rendererRef.current = null;
      uploadedRef.current = null;
      renderer.dispose();
    };
  }, [drawNow, ensureUploaded, surface]);

  useEffect(() => {
    liveRef.current = { photoId: photo?.id ?? null, image, recipe, options, view };
    schedule();
  }, [photo?.id, image, recipe, options, view, schedule]);

  useEffect(
    () => () => {
      window.cancelAnimationFrame(frameRef.current);
      window.clearTimeout(settleRef.current);
    },
    [],
  );

  // Statistics describe one frame of one photograph; they cannot outlive it.
  useEffect(() => {
    useStore.getState().setStats(null);
  }, [photo?.id]);

  // --- viewport size -------------------------------------------------------

  useEffect(() => {
    const viewport = viewportRef.current;
    const canvas = canvasRef.current;
    if (!viewport || !canvas) return;

    const measure = () => {
      const rect = viewport.getBoundingClientRect();
      if (rect.width === 0 || rect.height === 0) return;
      // Beyond 2× the extra pixels cost real milliseconds and buy nothing visible.
      const ratio = Math.min(window.devicePixelRatio || 1, 2);
      const deviceWidth = Math.max(1, Math.round(rect.width * ratio));
      const deviceHeight = Math.max(1, Math.round(rect.height * ratio));
      if (canvas.width !== deviceWidth || canvas.height !== deviceHeight) {
        canvas.width = deviceWidth;
        canvas.height = deviceHeight;
      }
      setSize((previous) =>
        previous.cssWidth === rect.width &&
        previous.cssHeight === rect.height &&
        previous.deviceWidth === deviceWidth &&
        previous.deviceHeight === deviceHeight
          ? previous
          : {
              cssWidth: rect.width,
              cssHeight: rect.height,
              deviceWidth,
              deviceHeight,
            },
      );

      // A pan that was legal at the old size may not be at the new one.
      const current = liveRef.current;
      const frame = processedRef.current;
      if (frame) {
        const place = placement(frame, deviceWidth, deviceHeight, current.view);
        const offsetX = settleOffset(place.offsetX, place.scaleX);
        const offsetY = settleOffset(place.offsetY, place.scaleY);
        const panX = offsetX - (1 - place.scaleX) / 2;
        const panY = (1 - place.scaleY) / 2 - offsetY;
        if (Math.abs(panX - current.view.panX) > 1e-4 || Math.abs(panY - current.view.panY) > 1e-4) {
          useStore.getState().setView({ panX, panY });
        }
      }
      schedule();
    };

    const observer = new ResizeObserver(measure);
    observer.observe(viewport);
    measure();
    return () => observer.disconnect();
  }, [schedule, surface]);

  // --- zoom and pan --------------------------------------------------------

  const zoomAbout = useCallback(
    (nextZoom: number, anchorX: number, anchorY: number) => {
      const canvas = canvasRef.current;
      const frame = processedRef.current;
      if (!canvas || !frame) return;
      const current = liveRef.current.view;
      const zoom = clamp(nextZoom, MIN_ZOOM, MAX_ZOOM);

      const from = placement(frame, canvas.width, canvas.height, current);
      const factor = zoom / current.zoom;
      const scaleX = from.fitX * zoom;
      const scaleY = from.fitY * zoom;
      const offsetX = settleOffset(anchorX - (anchorX - from.offsetX) * factor, scaleX);
      const offsetY = settleOffset(anchorY - (anchorY - from.offsetY) * factor, scaleY);

      const panX = offsetX - (1 - scaleX) / 2;
      const panY = (1 - scaleY) / 2 - offsetY;
      const fitted = Math.abs(zoom - 1) < 1e-4 && Math.abs(panX) < 1e-4 && Math.abs(panY) < 1e-4;
      setView({ zoom, panX, panY, mode: fitted ? 'fit' : 'free' });
    },
    [setView],
  );

  /** Zoom at which one pixel of the working image covers one screen pixel. */
  const actualZoom = useCallback(() => {
    const canvas = canvasRef.current;
    const frame = processedRef.current;
    if (!canvas || !frame) return 1;
    const place = placement(frame, canvas.width, canvas.height, liveRef.current.view);
    return frame.width / Math.max(1, place.fitX * canvas.width);
  }, []);

  const zoomPercent = useMemo(() => {
    if (!processed || size.deviceWidth === 0) return null;
    const place = placement(processed, size.deviceWidth, size.deviceHeight, view);
    return (place.fitX * view.zoom * size.deviceWidth) / processed.width;
  }, [processed, size, view]);

  const setZoomPercent = useCallback(
    (percent: number) => {
      zoomAbout((actualZoom() * percent) / 100, 0.5, 0.5);
    },
    [actualZoom, zoomAbout],
  );

  useEffect(() => {
    const viewport = viewportRef.current;
    if (!viewport) return;
    const onWheel = (event: WheelEvent) => {
      if (!processedRef.current) return;
      event.preventDefault();
      const rect = viewport.getBoundingClientRect();
      const unit = event.deltaMode === 1 ? 16 : event.deltaMode === 2 ? rect.height : 1;
      const factor = Math.exp((-event.deltaY * unit) / 420);
      zoomAbout(
        liveRef.current.view.zoom * factor,
        (event.clientX - rect.left) / rect.width,
        (event.clientY - rect.top) / rect.height,
      );
    };
    // React attaches wheel passively at the root, which cannot preventDefault.
    viewport.addEventListener('wheel', onWheel, { passive: false });
    return () => viewport.removeEventListener('wheel', onWheel);
  }, [zoomAbout]);

  useEffect(() => {
    const onZoom = (event: CustomEvent<'fit' | 'actual'>) => {
      if (event.detail === 'fit') zoomTo(1);
      else zoomAbout(actualZoom(), 0.5, 0.5);
    };
    window.addEventListener('lumos:zoom', onZoom);
    return () => window.removeEventListener('lumos:zoom', onZoom);
  }, [actualZoom, zoomAbout, zoomTo]);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.code !== 'Space' || event.repeat) return;
      const target = event.target;
      if (
        target instanceof HTMLElement &&
        (target.isContentEditable || /^(INPUT|TEXTAREA|BUTTON|SELECT)$/.test(target.tagName))
      ) {
        return;
      }
      // Space would otherwise scroll the rail out from under the panels.
      event.preventDefault();
      setSpaceHeld(true);
    };
    const onKeyUp = (event: KeyboardEvent) => {
      if (event.code === 'Space') setSpaceHeld(false);
    };
    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('keyup', onKeyUp);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('keyup', onKeyUp);
    };
  }, []);

  const canPan = useMemo(() => {
    if (!processed || size.deviceWidth === 0) return false;
    const place = placement(processed, size.deviceWidth, size.deviceHeight, view);
    return place.scaleX > 1.0001 || place.scaleY > 1.0001;
  }, [processed, size, view]);

  // --- before / after ------------------------------------------------------

  const wantedBeforeKey = photo && recipe ? `${photo.id}|${geometryKey(recipe)}` : null;

  useEffect(() => {
    if (!showBefore || !wantedBeforeKey || beforeKey === wantedBeforeKey) return;
    const renderer = rendererRef.current;
    const target = beforeRef.current;
    const live = liveRef.current;
    if (!renderer || !target || !live.image || !live.recipe || renderer.contextLost) return;

    try {
      ensureUploaded(renderer, live.image);
      renderer.process(neutralWithGeometry(live.recipe), live.image, CLEAN_OPTIONS);
      const frame = renderer.readProcessed();
      drawNow();
      if (!frame) return;
      target.width = frame.width;
      target.height = frame.height;
      const context = target.getContext('2d');
      if (!context) return;
      context.putImageData(toImageData(frame), 0, 0);
      setBeforeKey(wantedBeforeKey);
    } catch (error) {
      console.warn('Lumos could not render the unedited comparison', error);
    }
  }, [showBefore, wantedBeforeKey, beforeKey, drawNow, ensureUploaded]);

  const frameRect = useMemo(() => {
    if (!processed || size.cssWidth === 0 || size.deviceWidth === 0) return null;
    const place = placement(processed, size.deviceWidth, size.deviceHeight, view);
    return {
      left: place.offsetX * size.cssWidth,
      top: place.offsetY * size.cssHeight,
      width: place.scaleX * size.cssWidth,
      height: place.scaleY * size.cssHeight,
    };
  }, [processed, size, view]);

  // The divider, its labels and the unedited layer are one thing: none of them
  // may appear before the frame's position on screen is known.
  const comparing =
    showBefore && frameRect !== null && beforeKey !== null && beforeKey === wantedBeforeKey;
  const split = splitPosition ?? 1;
  const splitX = split * size.cssWidth;
  const beforeInset =
    frameRect && frameRect.width > 0
      ? clamp(1 - (splitX - frameRect.left) / frameRect.width, 0, 1) * 100
      : 100;
  /** Holding backslash shows the whole original, so there is nothing to drag. */
  const dividerShown = comparing && splitPosition !== null;

  useEffect(() => {
    if (!dividerShown) {
      setEntered(false);
      return;
    }
    if (reducedMotion()) {
      setEntered(true);
      return;
    }
    // One frame at the start of the move, so the browser has something to
    // animate from. After that the divider is wherever the hand put it.
    const frame = window.requestAnimationFrame(() => setEntered(true));
    return () => window.cancelAnimationFrame(frame);
  }, [dividerShown]);

  const startSplitDrag = (event: ReactPointerEvent<HTMLElement>, jump: boolean) => {
    const viewport = viewportRef.current;
    if (!viewport) return;
    event.preventDefault();
    event.stopPropagation();
    viewport.setPointerCapture(event.pointerId);
    dragRef.current = { kind: 'split', x: event.clientX, y: event.clientY };
    setSplitting(true);
    if (!jump) return;
    const rect = viewport.getBoundingClientRect();
    setSplitPosition(clamp((event.clientX - rect.left) / rect.width, SPLIT_MIN, SPLIT_MAX));
  };

  const onPointerDown = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (!image || event.button !== 0) return;
    // While the divider is up the whole stage is the control. Space still pans,
    // which is the gesture the hand already knows from every other editor.
    if (dividerShown && !spaceHeld) {
      startSplitDrag(event, true);
      return;
    }
    event.currentTarget.setPointerCapture(event.pointerId);
    dragRef.current = { kind: 'pan', x: event.clientX, y: event.clientY };
    setPanning(true);
  };

  const onPointerMove = (event: ReactPointerEvent<HTMLDivElement>) => {
    const drag = dragRef.current;
    const viewport = viewportRef.current;
    if (!drag || !viewport) return;
    const rect = viewport.getBoundingClientRect();

    if (drag.kind === 'split') {
      setSplitPosition(clamp((event.clientX - rect.left) / rect.width, SPLIT_MIN, SPLIT_MAX));
      return;
    }

    const frame = processedRef.current;
    const canvas = canvasRef.current;
    if (!frame || !canvas) return;
    const current = liveRef.current.view;
    const place = placement(frame, canvas.width, canvas.height, current);
    const offsetX = settleOffset(place.offsetX + (event.clientX - drag.x) / rect.width, place.scaleX);
    const offsetY = settleOffset(place.offsetY + (event.clientY - drag.y) / rect.height, place.scaleY);
    drag.x = event.clientX;
    drag.y = event.clientY;
    setView({
      panX: offsetX - (1 - place.scaleX) / 2,
      panY: (1 - place.scaleY) / 2 - offsetY,
      mode: 'free',
    });
  };

  const endDrag = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (!dragRef.current) return;
    dragRef.current = null;
    setPanning(false);
    setSplitting(false);
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
  };

  const onDoubleClick = (event: ReactMouseEvent<HTMLDivElement>) => {
    const viewport = viewportRef.current;
    if (!viewport || !processedRef.current) return;
    // Two quick moves of the divider must not also change the magnification.
    if (dividerShown) return;
    const rect = viewport.getBoundingClientRect();
    const actual = actualZoom();
    if (Math.abs(liveRef.current.view.zoom - actual) < actual * 0.01) {
      zoomTo(1);
      return;
    }
    zoomAbout(
      actual,
      (event.clientX - rect.left) / rect.width,
      (event.clientY - rect.top) / rect.height,
    );
  };

  const nudgeSplit = (event: ReactKeyboardEvent<HTMLDivElement>) => {
    if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
    event.preventDefault();
    event.stopPropagation();
    const nudge = (event.shiftKey ? 0.1 : 0.02) * (event.key === 'ArrowLeft' ? -1 : 1);
    setSplitPosition(clamp(split + nudge, SPLIT_MIN, SPLIT_MAX));
  };

  const toggleBefore = () => {
    if (showBefore) {
      setShowBefore(false);
      setSplitPosition(null);
      return;
    }
    setSplitPosition(0.5);
    setShowBefore(true);
  };

  // --- stage bar facts -----------------------------------------------------

  const capture = image?.capture;
  const dimensions = processed ? `${processed.width} × ${processed.height}` : '';
  const cameraFacts = capture
    ? [`${capture.cameraMake} ${capture.cameraModel}`.trim(), formatCapture(capture)]
        .filter(Boolean)
        .join('  ·  ')
    : '';
  const status = photo && photo.status !== 'ready' && !image ? photo : null;

  const notices: Notice[] = [];
  if (image) {
    if (image.fromEmbeddedPreview) {
      notices.push({
        key: 'preview',
        text: 'Camera preview only. Raw data could not be read.',
        tone: 'warn',
        tip: {
          title: 'Camera preview',
          body: "Edits apply to the camera's JPEG, which has far less highlight and white balance latitude than the raw file.",
        },
      });
    }
    for (const warning of image.warnings) {
      const notice = noticeFor(warning);
      if (notice && !notices.some((existing) => existing.key === notice.key)) notices.push(notice);
    }
  }

  const fitOn = Math.abs(view.zoom - 1) < 1e-4;
  const stopOn = (stop: number) =>
    !fitOn && zoomPercent !== null && Math.abs(zoomPercent * 100 - stop) < 0.5;
  // Fit says nothing about magnification, so the number still earns its place there.
  const showPercent = !ZOOM_STOPS.some(stopOn);

  // The divider arrives from the middle of the frame once, then never moves on
  // its own again: position is never transitioned, so a drag stays on the hand.
  const introLine: CSSProperties = {
    // Pinned rather than left to the flex static position, which browsers
    // resolve differently for an absolutely positioned child.
    left: '50%',
    opacity: entered ? 1 : 0,
    transform: entered ? 'scaleY(1)' : 'scaleY(0.05)',
    transition: 'opacity 150ms linear, transform 320ms cubic-bezier(0.22, 0.61, 0.36, 1)',
  };
  const introGrip: CSSProperties = {
    display: 'flex',
    opacity: entered ? 1 : 0,
    transform: entered ? 'scale(1)' : 'scale(0.7)',
    transition: 'opacity 200ms var(--ease), transform 300ms cubic-bezier(0.22, 0.61, 0.36, 1)',
  };
  const introTag: CSSProperties = {
    opacity: entered ? 1 : 0,
    transition: 'opacity 240ms var(--ease)',
  };

  return (
    <>
      <div
        ref={viewportRef}
        className={`viewport${panning ? ' grabbing' : canPan || spaceHeld ? ' grab' : ''}`}
        style={dividerShown && !spaceHeld ? { cursor: 'ew-resize' } : undefined}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={endDrag}
        onPointerCancel={endDrag}
        onDoubleClick={onDoubleClick}
      >
        <canvas key={surface} ref={canvasRef} />

        <canvas
          ref={beforeRef}
          aria-hidden="true"
          style={{
            position: 'absolute',
            display: comparing ? 'block' : 'none',
            left: frameRect ? `${frameRect.left}px` : 0,
            top: frameRect ? `${frameRect.top}px` : 0,
            width: frameRect ? `${frameRect.width}px` : 0,
            height: frameRect ? `${frameRect.height}px` : 0,
            clipPath: `inset(0 ${beforeInset}% 0 0)`,
            pointerEvents: 'none',
          }}
        />

        {dividerShown ? (
          <>
            {split > 0.12 ? (
              <span className="compare-tag before" style={introTag}>
                Before
              </span>
            ) : null}
            {split < 0.88 ? (
              <span className="compare-tag after" style={introTag}>
                After
              </span>
            ) : null}
            <div
              className={`compare-handle${splitting ? ' dragging' : ''}`}
              role="separator"
              aria-orientation="vertical"
              aria-label="Before and after divider"
              aria-valuenow={Math.round(split * 100)}
              aria-valuemin={Math.round(SPLIT_MIN * 100)}
              aria-valuemax={Math.round(SPLIT_MAX * 100)}
              aria-valuetext={`${Math.round(split * 100)}% edited`}
              tabIndex={0}
              style={{ left: `${splitX}px` }}
              onPointerDown={(event) => {
                // Grabbing the grip should also give it the arrow keys.
                event.currentTarget.focus();
                startSplitDrag(event, false);
              }}
              onKeyDown={nudgeSplit}
            >
              <div className="compare-line" style={introLine} />
              <span style={introGrip}>
                <span className="compare-grip" aria-hidden="true" />
              </span>
            </div>
          </>
        ) : null}

        {comparing && splitPosition === null ? (
          <span className="compare-tag before">Before</span>
        ) : null}

        {notices.length > 0 ? (
          <div className="notice-stack" style={comparing ? { top: 46 } : undefined}>
            {notices.map((notice) => {
              const className = notice.tone === 'warn' ? 'notice warn' : 'notice';
              return notice.tip ? (
                <Tip key={notice.key} title={notice.tip.title} body={notice.tip.body}>
                  {/* Focusable so the longer explanation is reachable without a pointer. */}
                  <span className={className} tabIndex={0}>
                    {notice.text}
                  </span>
                </Tip>
              ) : (
                <span className={className} key={notice.key}>
                  {notice.text}
                </span>
              );
            })}
          </div>
        ) : null}

        {initError ? (
          <div className="stage-status" style={{ background: 'var(--canvas)' }}>
            <div className="msg-error" style={{ maxWidth: 440, pointerEvents: 'auto' }} role="alert">
              <div style={{ marginBottom: 6 }}>Lumos cannot start its renderer here.</div>
              <div className="muted">{initError}</div>
              <div className="muted" style={{ marginTop: 8 }}>
                Lumos needs WebGL2 with floating-point render targets. Turning on hardware
                acceleration, or opening Lumos in a current Chrome, Edge, Firefox or Safari, usually
                fixes it.
              </div>
            </div>
          </div>
        ) : null}

        {!initError && contextLost ? (
          <div className="stage-status" style={{ background: 'var(--canvas)' }}>
            <div className="msg-error" style={{ maxWidth: 400, pointerEvents: 'auto' }} role="alert">
              <div style={{ marginBottom: 8 }}>
                The browser took the graphics context back to free memory. Your edits are safe.
              </div>
              <button type="button" className="btn tiny" onClick={() => window.location.reload()}>
                Reload Lumos
              </button>
            </div>
          </div>
        ) : null}

        {!initError && !contextLost && status?.status === 'error' ? (
          <div className="stage-status" style={{ background: 'var(--canvas)' }}>
            <div className="msg-error" style={{ maxWidth: 440, pointerEvents: 'auto' }} role="alert">
              <div style={{ marginBottom: 6 }}>{status.filename} could not be read.</div>
              <div className="muted" style={{ marginBottom: 8 }}>{status.error}</div>
              <button type="button" className="btn tiny" onClick={() => select(status.id)}>
                Try again
              </button>
            </div>
          </div>
        ) : null}

        {!initError && !contextLost && status && status.status !== 'error' ? (
          <div className="stage-status" style={{ background: 'var(--canvas)' }} aria-live="polite">
            <div className="spinner" aria-hidden="true" />
            <div className="label">{status.progress || 'Preparing'}</div>
            <div className="muted">{status.filename}</div>
          </div>
        ) : null}

        {!initError && !contextLost && renderError ? (
          <div className="stage-status">
            <div className="msg-error" style={{ maxWidth: 440, pointerEvents: 'auto' }} role="alert">
              <div style={{ marginBottom: 6 }}>The preview could not be rendered.</div>
              <div className="muted">{renderError}</div>
            </div>
          </div>
        ) : null}
      </div>

      <div className="stage-bar" style={{ overflow: 'hidden' }}>
        {/* The facts give way to the controls, and give up the name before the numbers. */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            flex: '1 1 auto',
            minWidth: 60,
            overflow: 'hidden',
            whiteSpace: 'nowrap',
          }}
        >
          <span
            style={{
              color: 'var(--cream-dim)',
              flex: '0 1 auto',
              minWidth: 0,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
            title={photo?.filename}
          >
            {photo?.filename ?? ''}
          </span>

          {dimensions ? (
            <Tip
              title="Preview size"
              body="The working preview after cropping and straightening. Export re-decodes at full size."
            >
              <span className="muted" style={{ flex: '0 0 auto' }}>
                {dimensions}
              </span>
            </Tip>
          ) : null}

          {cameraFacts ? (
            <span
              className="muted"
              style={{ flex: '0 1 auto', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}
            >
              {cameraFacts}
            </span>
          ) : null}
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: '0 0 auto' }}>
          <Tip
            title="Zoom"
            body="Fit the whole frame, or work at a fixed size. 100 shows one image pixel per screen pixel."
          >
            <span className="segmented" role="group" aria-label="Zoom">
              <button
                type="button"
                className={fitOn ? 'on' : ''}
                aria-pressed={fitOn}
                onClick={() => zoomTo(1)}
              >
                Fit
              </button>
              {ZOOM_STOPS.map((stop) => (
                <button
                  key={stop}
                  type="button"
                  className={stopOn(stop) ? 'on' : ''}
                  aria-pressed={stopOn(stop)}
                  aria-label={`Zoom to ${stop} percent`}
                  onClick={() => setZoomPercent(stop)}
                >
                  {stop}
                </button>
              ))}
            </span>
          </Tip>

          <span
            className="value"
            style={{ fontSize: 'inherit', minWidth: 34, textAlign: 'right' }}
            aria-live="off"
          >
            {showPercent && zoomPercent !== null ? `${Math.round(zoomPercent * 100)}%` : ''}
          </span>

          <Tip
            title="Compare"
            body="Drag the divider to see the photograph before your edits. Hold backslash for the whole original."
          >
            <button
              type="button"
              className={`btn tiny${showBefore ? ' active' : ''}`}
              aria-pressed={showBefore}
              onClick={toggleBefore}
              disabled={!image}
            >
              Compare
            </button>
          </Tip>

          <Tip
            title="Clipping"
            body="Red marks highlights with no detail left, blue marks crushed shadows. Press J."
          >
            <button
              type="button"
              className={`btn tiny${showClipping ? ' active' : ''}`}
              aria-pressed={showClipping}
              onClick={toggleClipping}
            >
              Clipping
            </button>
          </Tip>

          <Tip
            title="Render time"
            body="How long the last preview frame took to build. The GPU finishes it after this clock stops."
          >
            <span className="value" style={{ fontSize: 'inherit', minWidth: 44, textAlign: 'right' }}>
              {renderMs === null
                ? ''
                : `${renderMs < 10 ? renderMs.toFixed(1) : Math.round(renderMs)} ms`}
            </span>
          </Tip>
        </div>
      </div>
    </>
  );
}
